import { useCallback, useMemo, useRef } from "react";
import ReactFlow, { Background, Controls, Handle, Position } from "reactflow";
import "reactflow/dist/style.css";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";
import { Button, Tooltip, Typography } from "@mui/material";
import { resolveSelfLabel, bucketByRank, directionColorBucket } from "./cdrEngine";

export function computeRadialLayout(contacts, devices) {
  const contactList = contacts || [];
  const deviceList = devices || [];

  const contactRadius = 220 + Math.max(0, contactList.length - 10) * 4;
  const contactNodes = contactList.map((c, i) => {
    const angle = (2 * Math.PI * i) / Math.max(1, contactList.length) - Math.PI / 2;
    return { ...c, x: contactRadius * Math.cos(angle), y: contactRadius * Math.sin(angle) };
  });

  const deviceRadius = 110;
  const arcStart = Math.PI * 0.65;
  const arcEnd = Math.PI * 1.1;
  const deviceNodes = deviceList.map((d, i) => {
    const t = deviceList.length > 1 ? i / (deviceList.length - 1) : 0.5;
    const angle = arcStart + (arcEnd - arcStart) * t;
    return { ...d, x: deviceRadius * Math.cos(angle), y: deviceRadius * Math.sin(angle) };
  });

  return { contactNodes, deviceNodes };
}

const EDGE_WIDTH_BY_BUCKET = { large: 4, medium: 2.5, small: 1.5 };
const COLOR_BY_DIRECTION = { in: "#16a34a", out: "#ea580c", balanced: "#6b7280" };

export function buildNetworkGraphData(filteredAnalysis, filteredNormRows, topN) {
  const contacts = (filteredAnalysis?.mfcAll || []).slice(0, topN);
  const devices = (filteredAnalysis?.imeiSummary || [])
    .slice()
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  if (!contacts.length) {
    return { nodes: [], edges: [], isEmpty: true };
  }

  const selfLabel = resolveSelfLabel(filteredNormRows);
  const { contactNodes, deviceNodes } = computeRadialLayout(contacts, devices);

  const nodes = [
    {
      id: "self",
      type: "cdrNode",
      position: { x: 0, y: 0 },
      data: { kind: "self", label: selfLabel, tooltip: `Subscriber: ${selfLabel}` },
      draggable: false,
    },
    ...contactNodes.map((c, i) => ({
      id: c.bParty,
      type: "cdrNode",
      position: { x: c.x, y: c.y },
      data: {
        kind: "contact",
        label: c.bParty,
        rawLabel: c.bParty,
        sizeBucket: bucketByRank(i, contactNodes.length),
        colorBucket: directionColorBucket(c),
        tooltip: `In: ${c.inCall} calls, ${c.inSms} sms · Out: ${c.outCall} calls, ${c.outSms} sms · Total: ${c.total}`,
      },
      draggable: false,
    })),
    ...deviceNodes.map((d) => ({
      id: `imei:${d.imei}`,
      type: "cdrNode",
      position: { x: d.x, y: d.y },
      data: {
        kind: "device",
        label: (d.imei || "").slice(-6),
        rawLabel: d.imei,
        tooltip: `IMEI: ${d.imei} · Records: ${d.count} · From: ${d.from ? d.from.toLocaleDateString() : "-"} · To: ${d.to ? d.to.toLocaleDateString() : "-"}`,
      },
      draggable: false,
    })),
  ];

  const edges = [
    ...contactNodes.map((c, i) => ({
      id: `edge-contact-${c.bParty}`,
      source: "self",
      target: c.bParty,
      style: {
        stroke: COLOR_BY_DIRECTION[directionColorBucket(c)],
        strokeWidth: EDGE_WIDTH_BY_BUCKET[bucketByRank(i, contactNodes.length)],
      },
    })),
    ...deviceNodes.map((d) => ({
      id: `edge-device-${d.imei}`,
      source: "self",
      target: `imei:${d.imei}`,
      style: { stroke: "#9ca3af", strokeWidth: 1.5, strokeDasharray: "4 3" },
    })),
  ];

  return { nodes, edges, isEmpty: false };
}

const NODE_DIAMETER_BY_SIZE = { large: 64, medium: 48, small: 36 };

function CdrGraphNode({ data }) {
  const { kind, label, subLabel, tooltip } = data;
  let diameter = 80;
  let background = "#111827";
  let borderRadius = "50%";

  if (kind === "contact") {
    diameter = NODE_DIAMETER_BY_SIZE[data.sizeBucket] || 48;
    background = COLOR_BY_DIRECTION[data.colorBucket] || "#6b7280";
  } else if (kind === "device") {
    diameter = 40;
    background = "#7c8b9a";
    borderRadius = "8px";
  }

  const content = (
    <div
      style={{
        width: diameter,
        height: diameter,
        borderRadius,
        background,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        textAlign: "center",
        padding: 4,
        overflow: "hidden",
        cursor: kind === "self" || kind === "hub" ? "default" : "pointer",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <div>{label}</div>
      {subLabel && <div style={{ fontSize: 8, fontWeight: 400, opacity: 0.85 }}>{subLabel}</div>}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );

  if (kind === "self" || kind === "hub") return content;
  return (
    <Tooltip title={tooltip} placement="top">
      {content}
    </Tooltip>
  );
}

export const NODE_TYPES = { cdrNode: CdrGraphNode };

export default function CDRNetworkGraph({ filteredAnalysis, filteredNormRows, topN, onSelectContact, onSelectDevice }) {
  const graphData = useMemo(
    () => buildNetworkGraphData(filteredAnalysis, filteredNormRows, topN),
    [filteredAnalysis, filteredNormRows, topN]
  );
  const flowWrapRef = useRef(null);

  const handleNodeClick = useCallback(
    (event, node) => {
      if (node.data.kind === "contact" && onSelectContact) onSelectContact(node.data.rawLabel);
      if (node.data.kind === "device" && onSelectDevice) onSelectDevice(node.data.rawLabel);
    },
    [onSelectContact, onSelectDevice]
  );

  const exportPNG = useCallback(async () => {
    const el = flowWrapRef.current?.querySelector(".react-flow");
    if (!el) return;
    const dataUrl = await toPng(el, { backgroundColor: "#ffffff", pixelRatio: 2 });
    saveAs(dataUrl, `CDR_Network_${Date.now()}.png`);
  }, []);

  if (graphData.isEmpty) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts to graph (check filters).
      </Typography>
    );
  }

  return (
    <div>
      <Button size="small" variant="outlined" onClick={exportPNG} sx={{ mb: 1 }}>
        Export PNG
      </Button>
      <div
        ref={flowWrapRef}
        style={{ width: "100%", height: 520, border: "1px solid #e5e7eb", borderRadius: 12 }}
      >
        <ReactFlow
          nodes={graphData.nodes}
          edges={graphData.edges}
          nodeTypes={NODE_TYPES}
          nodesDraggable={false}
          nodesConnectable={false}
          onNodeClick={handleNodeClick}
          onInit={(instance) => instance.fitView({ padding: 0.2 })}
          fitView
          style={{ width: "100%", height: "100%", minHeight: 520 }}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  );
}
